import React, { useMemo } from 'react';
import { AutoComplete, Input, Typography } from 'antd';

const { Text } = Typography;

// A free-text box that also offers what this doctor has written before. Picking a
// suggestion fills the box and leaves it completely editable - the doctor adjusts
// the wording for this patient, keeps it as it stands, or ignores the list and
// writes something new.
//
// Takes `value` / `onChange` from the Form.Item it sits in.
//
// `autoSize` is passed straight through to the text area, so the dense visit
// form can start these two rows deep and let them grow as they are written
// into, rather than reserving room for a paragraph nobody may write.
const SuggestingTextArea = ({ suggestions = [], rows = 3, autoSize, placeholder, value, onChange }) => {
    const options = useMemo(() => suggestions.map((suggestion) => ({
        value: suggestion.value,
        label: (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                {/* Complaints run long; let them wrap instead of being cut off. */}
                <span style={{ whiteSpace: 'normal' }}>{suggestion.value}</span>
                {suggestion.count > 1 && (
                    <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                        &times;{suggestion.count}
                    </Text>
                )}
            </div>
        )
    })), [suggestions]);

    return (
        <AutoComplete
            value={value}
            onChange={onChange}
            options={options}
            popupMatchSelectWidth={420}
            // Nothing is highlighted until the doctor arrows onto it, so Enter puts a
            // newline in the box the way it does in any other text area instead of
            // silently replacing what they are typing with a suggestion.
            defaultActiveFirstOption={false}
            filterOption={(input, option) =>
                option.value.toLowerCase().includes((input || '').toLowerCase())
            }
        >
            <Input.TextArea
                {...(autoSize ? { autoSize } : { rows })}
                placeholder={placeholder}
            />
        </AutoComplete>
    );
};

export default SuggestingTextArea;
